import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { withoutBrief } from "@/lib/company-tag";
import { chatRecentCompaniesKeys } from "@/lib/query-keys";
import { companyWebPreviewKey } from "@/stores/company-web-preview-store";
import type { CompanyPreviewViewRequestDTO, CompanyTag } from "@shaarp/common";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Records in the user's history that the preview panel of this tagged company
 * was opened (`POST /company/preview/view`): once per opening, and again when
 * the panel steps to another company. A reopened panel is a new mount, so it
 * is recorded even when the web profile comes from the client's cache — which
 * is why the preview's own stream does not record it.
 *
 * The ref keeps a re-render with a new tag object for the same company, and
 * the effect run twice by `StrictMode`, from recording twice.
 *
 * Once recorded, the chat home's recently viewed companies are read again
 * rather than after their minute of `staleTime`: the company just previewed
 * is there on the way back.
 *
 * Fire and forget: the panel never waits for it, and a failed record (the
 * throttle, the network) tells the user nothing they could act on, so it is
 * dropped without a toast — it only costs one entry of "Consultées
 * récemment".
 */
export function useRecordCompanyPreviewView(tag: CompanyTag): void {
  const api = useApi();
  const queryClient = useQueryClient();
  const { teamSelected } = useTeams();
  const key = companyWebPreviewKey(teamSelected?.id, tag);

  // The latest tag, read by the effect without re-running it.
  const latestTag = useRef(tag);
  latestTag.current = tag;
  const recordedKey = useRef<string | null>(null);

  useEffect(() => {
    if (recordedKey.current === key) return;
    recordedKey.current = key;

    const body: CompanyPreviewViewRequestDTO = {
      tag: withoutBrief(latestTag.current),
    };
    api
      .post("/company/preview/view", body)
      .then(() =>
        queryClient.invalidateQueries({
          queryKey: chatRecentCompaniesKeys.all,
        }),
      )
      .catch(() => undefined);
  }, [api, queryClient, key]);
}
